import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Plus, Download, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api, { API } from "../lib/api";
import { toast } from "sonner";

/**
 * VolunteerHoursPanel (donor portal) — self-service hour tracking + certificate download.
 * Used on /account.
 */
const VolunteerHoursSelfPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [logging, setLogging] = useState(false);
    const [form, setForm] = useState({
        hours: "",
        date: new Date().toISOString().slice(0, 10),
        activity: "",
        notes: "",
    });

    const load = () =>
        api.get("/my/volunteer-hours")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLogging(true);
        try {
            await api.post("/my/volunteer-hours", {
                hours: parseFloat(form.hours),
                date: form.date,
                activity: form.activity,
                notes: form.notes,
            });
            toast.success(`Logged ${form.hours} hours — pending board verification`);
            setForm({ ...form, hours: "", activity: "", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't log hours");
        } finally {
            setLogging(false);
        }
    };

    if (loading) return null;
    const total = data?.total_hours || 0;
    const verified = data?.verified_hours || 0;
    const entries = data?.entries || [];

    return (
        <div className="mt-12" data-testid="account-volunteer-section">
            <div className="mb-6 flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Volunteer hours</h2>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl p-6">
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div>
                        <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold">Total logged</p>
                        <p className="font-heading text-3xl text-[#1C252E] font-medium mt-1" data-testid="account-volunteer-total-hours">{total.toFixed(1)} hrs</p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold">Board-verified</p>
                        <p className="font-heading text-3xl text-[#E53935] font-medium mt-1" data-testid="account-volunteer-verified-hours">{verified.toFixed(1)} hrs</p>
                    </div>
                    <div className="self-center">
                        {total > 0 ? (
                            <a
                                href={`${API}/my/volunteer-hours/certificate.pdf`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="account-volunteer-certificate-btn"
                                className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                            >
                                <Download className="w-4 h-4" strokeWidth={1.5} />
                                Service-hour certificate
                            </a>
                        ) : (
                            <p className="text-xs text-[#4A5568]">Log your first hour to unlock the certificate.</p>
                        )}
                    </div>
                </div>

                {/* Log form */}
                <form onSubmit={submit} className="bg-[#FDFBF7] rounded-xl p-4 mb-4 grid sm:grid-cols-4 gap-2" data-testid="account-volunteer-log-form">
                    <input
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="24"
                        required
                        placeholder="Hours"
                        value={form.hours}
                        onChange={(e) => setForm({ ...form, hours: e.target.value })}
                        data-testid="account-volunteer-hours-input"
                        aria-label="Hours"
                        className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                    />
                    <input
                        type="date"
                        required
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        data-testid="account-volunteer-date-input"
                        aria-label="Date"
                        className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                    />
                    <input
                        type="text"
                        required
                        placeholder="Activity (e.g. PTA cleanup)"
                        maxLength={200}
                        value={form.activity}
                        onChange={(e) => setForm({ ...form, activity: e.target.value })}
                        data-testid="account-volunteer-activity-input"
                        aria-label="Activity"
                        className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                    />
                    <button
                        type="submit"
                        disabled={logging}
                        data-testid="account-volunteer-log-btn"
                        className="inline-flex items-center justify-center gap-1.5 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium"
                    >
                        {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Log
                    </button>
                </form>

                {/* Entries */}
                {entries.length > 0 && (
                    <ul className="divide-y divide-black/5" data-testid="account-volunteer-list">
                        {entries.slice(0, 10).map((e) => (
                            <li key={e.id} className="flex items-center justify-between py-2.5 text-sm">
                                <div className="flex items-center gap-3 min-w-0">
                                    {e.status === "verified" ? (
                                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2} />
                                    ) : (
                                        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={2} />
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-[#1C252E] truncate">{e.activity}</p>
                                        <p className="text-xs text-[#4A5568]">{e.date} · {e.status === "verified" ? "verified" : "pending verification"}</p>
                                    </div>
                                </div>
                                <p className="font-medium text-[#1C252E] flex-shrink-0">{parseFloat(e.hours || 0).toFixed(1)} hrs</p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default VolunteerHoursSelfPanel;
